import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { RootState } from '../../redux/store';
import { downloadReport } from '../../utils/reportUtils';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { academicYears } from '../../constants/academicYears';
import { ALLOWED_CASH_FLOW_USER_EMAILS } from './utils/allowedUsers';
import CashFlowStatementPage from './CashFlowStatementPage';
import { getCashOutflowReport } from '../../apis/payments/cashFlowStatement';
import { getPaymentUser } from '../../apis/payments/users';
import { PaymentUser } from '../../types/userType';

const DEFAULT_SCHOOL_OPTION = { label: 'All', value: 'All' };
const DEFAULT_ACADEMIC_YEAR_OPTION = { label: 'All', value: 'All' };

const CashFlowStatementContainer: React.FC = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [academicYear, setAcademicYear] = useState(
    DEFAULT_ACADEMIC_YEAR_OPTION.value
  );
  const [school, setSchool] = useState(DEFAULT_SCHOOL_OPTION.value);
  const [paymentUser, setPaymentUser] = useState<PaymentUser | null>(null);
  const [isSchoolLoading, setIsSchoolLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const handleCloseToast = () => {
    setToastOpen(false);
  };
  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    []
  );

  useEffect(() => {
    if (!ALLOWED_CASH_FLOW_USER_EMAILS.includes(loggedInUser.email)) {
      console.error('Unauthorized Access!');
      showToast(
        'Unauthorized Access to Cash Flow Statement.',
        SnackbarSeverityType.error
      );
      setIsAuthorized(false);
      return;
    }
    setIsAuthorized(true);
  }, [loggedInUser.email, showToast]);

  const academicYearOptions = [DEFAULT_ACADEMIC_YEAR_OPTION, ...academicYears];

  const schoolOptions = paymentUser?.schools
    ? [
        DEFAULT_SCHOOL_OPTION,
        ...paymentUser.schools.map((s: string) => ({ label: s, value: s }))
      ]
    : [];

  useEffect(() => {
    setIsSchoolLoading(true);
    getPaymentUser()
      .then((user) => {
        setPaymentUser(user);
      })
      .catch((err) => {
        showToast(
          'Failed to load payment user schools.',
          SnackbarSeverityType.error
        );
      })
      .finally(() => setIsSchoolLoading(false));
  }, [showToast]);

  const fetchAllReportData = useCallback(async () => {
    setLoading(true);
    let allReportData: any[] = [];
    const pageSize = 400;
    let pageNo = 1;
    const BATCH_SIZE = 3; // Number of parallel calls
    let hasMoreData = true;

    const getReportPage = async (page: number) => {
      const response = await getCashOutflowReport(
        page,
        pageSize,
        school === DEFAULT_SCHOOL_OPTION.value ? undefined : school,
        academicYear === DEFAULT_ACADEMIC_YEAR_OPTION.value
          ? undefined
          : academicYear
      );
      return Array.isArray(response) ? response : [];
    };

    try {
      do {
        const pagePromises = [];
        for (let i = 0; i < BATCH_SIZE; i++) {
          pagePromises.push(getReportPage(pageNo + i));
        }

        const results = await Promise.all(pagePromises);
        let batchHasData = false;

        results.forEach((result) => {
          if (result.length > 0) {
            allReportData.push(...result);
            batchHasData = true;
          }
        });

        pageNo += BATCH_SIZE;
        hasMoreData = batchHasData;
      } while (hasMoreData);
      return allReportData;
    } catch (error) {
      console.error('Error fetching cash outflow report:', error);
      showToast(
        'Failed to fetch cash flow report.',
        SnackbarSeverityType.error
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [academicYear, school, showToast]);

  const handleDownloadReport = async () => {
    const currentReportData = await fetchAllReportData();
    const fileName = `Cash Flow Statement - ${moment().format('DD-MM-YYYY')}`;
    downloadReport(
      currentReportData,
      fileName,
      'Cash Flow Statement',
      showToast
    );
  };

  return (
    <>
      <CashFlowStatementPage
        academicYear={academicYear}
        school={school}
        academicYearOptions={academicYearOptions}
        schoolOptions={schoolOptions}
        loading={loading}
        isSchoolLoading={isSchoolLoading}
        onAcademicYearChange={setAcademicYear}
        onSchoolChange={setSchool}
        onDownloadReport={handleDownloadReport}
        isAuthorized={isAuthorized}
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default CashFlowStatementContainer;
